import { GraphicPrimitive } from './GraphicPrimitive';

export class CrossArea extends GraphicPrimitive {
  constructor(x, y, cols, rows, crossSize, color, fillColor, cells = null, colorPalette = null) {
    super(color, fillColor, 'parallel', 2, 45, 5, 'simple', 'CrossArea');
    this.x = x;
    this.y = y;
    this.cols = cols;
    this.rows = rows;
    this.crossSize = crossSize;
    this.viewMode = 'stitch'; // 'image', 'color', 'stitch', 'icon'
    this.m_isDrawing = false;

    // Initialize cells if not provided
    if (cells && cells.length > 0) {
      // Deep copy cells to avoid reference issues
      this.cells = cells.map(row => [...row]);
    } else {
      // Initialize all cells to color index 1 (current fillColor) by default
      this.cells = Array.from({ length: rows }, () => Array(cols).fill(0));
    }

    // Initialize color palette if not provided
    // Check for valid palette (must be an array with at least 2 entries: 0=transparent, 1+=colors)
    if (colorPalette && Array.isArray(colorPalette) && colorPalette.length > 0) {
      // Deep copy palette to avoid reference issues
      this.colorPalette = colorPalette.map(p => ({ ...p }));
    } else {
      // Default palette: 0 is transparent/empty, 1 is current fillColor
      this.colorPalette = [
        { color: 'transparent', color_id: 0, NoDraw: true },
        { color: fillColor || '#000000', color_id: 1, NoDraw: false }
      ];
    }

    this.m_isDrawing = false;
    this.regenerateStitches();
  }

  toJSON() {
    return {
      ...super.toJSON(),
      x: this.x,
      y: this.y,
      cols: this.cols,
      rows: this.rows,
      crossSize: this.crossSize,
      cells: this.cells.map(row => [...row]), // Deep copy cells
      colorPalette: this.colorPalette.map(p => ({ ...p })), // Deep copy palette
      viewMode: this.viewMode
    };
  }

  getControlPoints() {
    return [
      { x: this.x, y: this.y },
      { x: this.x + this.cols * this.crossSize, y: this.y + this.rows * this.crossSize }
    ];
  }

  updateControlPoint(pointIndex, newX, newY) {
    if (pointIndex === 0) {
      this.x = newX;
      this.y = newY;
    }
    // Note: Resizing via control point 1 might need logic to change cols/rows or crossSize
    // For now, let's just allow moving the top-left corner
    this.regenerateStitches();
  }

  move(dx, dy) {
    this.x += dx;
    this.y += dy;
    this.regenerateStitches();
  }

  regenerateStitches() {
    // Convert cells to actual stitches for export/simulation
    this.stitchLists = [];
    
    for (let row = 0; row < this.rows; row++) {
      for (let col = 0; col < this.cols; col++) {
        const colorIndex = this.cells[row][col];
        if (colorIndex > 0 && this.colorPalette[colorIndex] && !this.colorPalette[colorIndex].NoDraw) {
          const color = this.colorPalette[colorIndex].color;
          const cx = this.x + col * this.crossSize;
          const cy = this.y + row * this.crossSize;
          const cs = this.crossSize;

          // Standard cross stitch: two diagonal lines
          const stitch1 = [
            { x: cx, y: cy },
            { x: cx + cs, y: cy + cs }
          ];
          stitch1.color = color;

          const stitch2 = [
            { x: cx + cs, y: cy },
            { x: cx, y: cy + cs }
          ];
          stitch2.color = color;

          this.stitchLists.push(stitch1, stitch2);
        }
      }
    }
  }

  renderDrawing(ctx, transform, globalStitchIndex, stitchOffset) {
    const cs = this.crossSize;

    ctx.save();
    ctx.translate(this.x, this.y);

    // Draw white background for the cross area
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, this.cols * cs, this.rows * cs);

    // Draw border around cross area
    ctx.strokeStyle = '#ff6600';
    ctx.lineWidth = 1 / transform.scale;
    ctx.strokeRect(0, 0, this.cols * cs, this.rows * cs);

    // Draw grid lines that match cross cell boundaries
    ctx.strokeStyle = '#888888';
    ctx.lineWidth = 0.5 / transform.scale;
    
    // Vertical grid lines
    for (let col = 0; col <= this.cols; col++) {
      ctx.beginPath();
      ctx.moveTo(col * cs, 0);
      ctx.lineTo(col * cs, this.rows * cs);
      ctx.stroke();
    }
    
    // Horizontal grid lines
    for (let row = 0; row <= this.rows; row++) {
      ctx.beginPath();
      ctx.moveTo(0, row * cs);
      ctx.lineTo(this.cols * cs, row * cs);
      ctx.stroke();
    }

    // Render crosses
    for (let row = 0; row < this.rows; row++) {
      for (let col = 0; col < this.cols; col++) {
        const colorIndex = this.cells[row][col];
        if (colorIndex >= 0 && this.colorPalette[colorIndex] && !this.colorPalette[colorIndex].NoDraw) {
          const color = this.colorPalette[colorIndex].color;
          const cellX = col * cs;
          const cellY = row * cs;

          if (this.viewMode === 'color') {
            ctx.fillStyle = color;
            ctx.fillRect(cellX, cellY, cs + 0.5, cs + 0.5);
          } else if (this.viewMode === 'stitch') {
            ctx.fillStyle = color;

            // First diagonal ellipse
            ctx.beginPath();
            ctx.ellipse(cellX + cs / 2, cellY + cs / 2, cs * 0.5, cs * 0.1, Math.PI / 4, 0, 2 * Math.PI);
            ctx.fill();

            // Second diagonal ellipse
            ctx.beginPath();
            ctx.ellipse(cellX + cs / 2, cellY + cs / 2, cs * 0.5, cs * 0.1, -Math.PI / 4, 0, 2 * Math.PI);
            ctx.fill();
          }
          // Icon and Image modes can be added here if needed
        }
      }
    }

    // Draw selection boundary
    if (this.isSelected) {
      ctx.strokeStyle = 'red';
      ctx.lineWidth = 2 / transform.scale;
      ctx.strokeRect(0, 0, this.cols * cs, this.rows * cs);
    }

    ctx.restore();

    // If simulating, we could use drawStitches here, but the elliptic rendering is usually preferred for crosses
    // Unless in reality mode?
    if (GraphicPrimitive.realityMode && this.viewMode === 'stitch') {
        // We could use this.drawStitches(ctx, transform, globalStitchIndex, stitchOffset);
        // But regenerateStitches created a lot of lists.
    }
  }

  isPointInside(px, py, transform) {
    const lx = px - this.x;
    const ly = py - this.y;
    return lx >= 0 && lx <= this.cols * this.crossSize && ly >= 0 && ly <= this.rows * this.crossSize;
  }

  getCellAt(px, py) {
    const lx = px - this.x;
    const ly = py - this.y;
    const col = Math.floor(lx / this.crossSize);
    const row = Math.floor(ly / this.crossSize);
    if (col >= 0 && col < this.cols && row >= 0 && row < this.rows) {
      return { row, col };
    }
    return null;
  }

  paintCell(row, col, colorIndex) {
    if (row >= 0 && row < this.rows && col >= 0 && col < this.cols) {
      this.cells[row][col] = colorIndex;
      this.regenerateStitches();
      return true;
    }
    return false;
  }

  eraseCell(row, col) {
    return this.paintCell(row, col, 0);
  }

  setIsDrawing(dr) { this.m_isDrawing = dr; }
  isDrawing() { return this.m_isDrawing; }
  isComplete() { return true; } // CrossArea is always complete when created

  AddPoint(point) {
    this.x = point.x;
    this.y = point.y;
    this.m_isDrawing = false; // CrossArea doesn't use two-click flow
    return false;
  }

  AddSecondPoint(point) {
    // When created from dialog, cols/rows are already set - just ensure cells are initialized
    // The x,y position was set in AddPoint (top-left corner)

    // Ensure minimum size of 1x1
    if (this.cols <= 0) this.cols = 1;
    if (this.rows <= 0) this.rows = 1;

    // Reinitialize cells array if needed
    if (!this.cells || this.cells.length !== this.rows) {
      this.cells = Array.from({ length: this.rows }, () => Array(this.cols).fill(0));
    }

    this.m_isDrawing = false;
    this.regenerateStitches();
    return true;
  }
}